const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const SESSION_KEY = "wb_analytics_session";
const API_URL = process.env.REACT_APP_BASE_URL;

let eventQueue = [];
let flushTimer = null;
let isInitialized = false;

// In-memory deduplication map to prevent double-logging from rapid clicks or component+action dispatches
const recentEvents = new Map();
const DEDUPE_WINDOW_MS = 1200;

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const getUserId = () => {
  try {
    const stored =
      localStorage.getItem("userData") || sessionStorage.getItem("userData");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.id || parsed?.user_id || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

const getAuthToken = () => {
  try {
    return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  } catch (e) {
    return "";
  }
};

export const pushGtmPageView = (overrides = {}) => {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "page_view",
      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
      ...overrides,
    });
  } catch (e) {
    // analytics should never break the app
  }
};

const sendToGtm = (eventName, properties = {}) => {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...properties,
    });
  } catch (e) {
    // analytics should never break the app
  }
};

const initGtmDataLayer = () => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
};

export const trackEvent = (eventName, eventCategory, properties = {}) => {
  if (!eventName || !eventCategory) return;

  // Deduplication guard against rapid double-clicks or component + redux duplicate dispatches
  const now = Date.now();
  const propKey =
    properties?.job_id ||
    properties?.plan_id ||
    properties?.page_name ||
    properties?.invite_code ||
    (typeof properties === "object" ? JSON.stringify(properties) : "");
  const dedupeKey = `${eventName}:${propKey}`;
  const lastFired = recentEvents.get(dedupeKey);
  if (lastFired && now - lastFired < DEDUPE_WINDOW_MS) {
    return;
  }
  recentEvents.set(dedupeKey, now);

  // Clean up old dedupe keys periodically
  if (recentEvents.size > 200) {
    for (const [k, time] of recentEvents.entries()) {
      if (now - time > 10000) {
        recentEvents.delete(k);
      }
    }
  }

  sendToGtm(eventName, {
    event_category: eventCategory,
    ...properties,
  });

  const event = {
    event_name: eventName,
    event_category: eventCategory,
    properties,
    user_id: getUserId(),
    session_id: getSessionId(),
    page_url: window.location.href,
    referrer_url: document.referrer || null,
    timestamp: new Date().toISOString().slice(0, 19).replace("T", " "),
  };

  eventQueue.push(event);

  // Clicks, payments, and actions must not wait for the 5s batch
  if (eventName !== "page_view" || eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }
};

export const trackPageView = (pageName, extraProps = {}) => {
  trackEvent("page_view", "navigation", {
    page_name: pageName,
    page_path: window.location.pathname,
    ...extraProps,
  });
};

/* =========================================================================
   PILLAR 1: USER ONBOARDING & SIGN-UP FUNNEL
   ========================================================================= */
export const trackSignupPageView = (extraProps = {}) => {
  trackEvent("signup_page_viewed", "onboarding", {
    page_path: window.location.pathname,
    referrer: document.referrer || null,
    ...extraProps,
  });
};

export const trackSignupAttributed = (extraProps = {}) => {
  let utmProps = {};
  if (typeof window !== "undefined" && window.location) {
    try {
      const params = new URLSearchParams(window.location.search);
      utmProps = {
        utm_source: params.get("utm_source") || null,
        utm_medium: params.get("utm_medium") || null,
        utm_campaign: params.get("utm_campaign") || null,
        utm_content: params.get("utm_content") || null,
        utm_term: params.get("utm_term") || null,
        referrer: document.referrer || null,
      };
    } catch (e) {
      // ignore
    }
  }
  trackEvent("user_signup_attributed", "onboarding", {
    ...utmProps,
    ...extraProps,
  });
};

export const trackSignupCompleted = (extraProps = {}) => {
  trackEvent("user_signup_completed", "onboarding", extraProps);
};

export const trackAccountActivated = (extraProps = {}) => {
  trackEvent("user_account_activated", "onboarding", extraProps);
};

export const trackSignupDropoff = (lastStepFocused, extraProps = {}) => {
  trackEvent("signup_dropoffs", "onboarding", {
    last_step_focused: lastStepFocused,
    ...extraProps,
  });
};

/* =========================================================================
   PILLAR 2: EMPLOYER JOB POSTING FLOW
   ========================================================================= */
export const trackPostJobClick = (extraProps = {}) => {
  trackEvent("post_job_clicks", "employer", extraProps);
};

export const trackJobUploadExcelClick = (extraProps = {}) => {
  trackEvent("jobupload_excel_clicks", "employer", extraProps);
};

export const trackJobPostNewClick = (extraProps = {}) => {
  trackEvent("jobpost_new_clicks", "employer", extraProps);
};

export const trackJobPostPublishClick = (extraProps = {}) => {
  trackEvent("jobpost_publish_clicks", "employer", extraProps);
};

export const trackFirstJobPosted = (extraProps = {}) => {
  trackEvent("first_job_posted", "employer", extraProps);
};

export const trackJobPostVerificationClick = (extraProps = {}) => {
  trackEvent("jobpost_verification_clicks", "employer", extraProps);
};

/* =========================================================================
   PILLAR 3: CANDIDATE REFERRAL ENGINE
   ========================================================================= */
export const trackReferButtonClick = (jobId, extraProps = {}) => {
  trackEvent("refer_button_clicks", "referrals", {
    job_id: jobId,
    ...extraProps,
  });
};

export const trackReferralLinkCopied = (jobId, extraProps = {}) => {
  trackEvent("referral_link_copied", "referrals", {
    job_id: jobId,
    ...extraProps,
  });
};

export const trackFirstReferralCompleted = (jobId, extraProps = {}) => {
  trackEvent("first_referral_completed", "referrals", {
    job_id: jobId,
    ...extraProps,
  });
};

export const trackUserReferralSuccess = (jobId, extraProps = {}) => {
  trackEvent("user_referral_success", "referrals", {
    job_id: jobId,
    ...extraProps,
  });
};

/* =========================================================================
   PILLAR 4: SUBSCRIPTIONS & MONETIZATION
   ========================================================================= */
export const trackJobMatchScoreClick = (extraProps = {}) => {
  trackEvent("job_match_score_clicks", "monetization", {
    feature: "match_score",
    ...extraProps,
  });
};

export const trackJobMatchScoreCompleted = (jobId, score, extraProps = {}) => {
  trackEvent("job_match_score_completed", "monetization", {
    job_id: jobId,
    score: score !== null && score !== undefined ? score : 0,
    ...extraProps,
  });
};

export const trackMatchScore = (jobTitle, score, status = "success", extraProps = {}) => {
  trackEvent("match_score_run", "monetization", {
    job_title: jobTitle,
    score: score !== null && score !== undefined ? score : 0,
    status, // "success" or "failed"
    ...extraProps,
  });

  if (status === "success") {
    trackEvent("job_match_score_completed", "monetization", {
      job_title: jobTitle,
      job_id: extraProps?.job_id || null,
      score: score !== null && score !== undefined ? score : 0,
      is_first_time: extraProps?.is_first_time ?? false,
      is_paid_scan: extraProps?.is_paid_scan ?? false,
      ...extraProps,
    });
  }
};

export const trackMatchScoreUpgradeClick = (extraProps = {}) => {
  trackEvent("match_score_upgrade_clicks", "monetization", extraProps);
};

export const trackBuyScanClick = (page = "job_match_score") => {
  trackEvent("buy_scan_click", "monetization", { page });
  trackMatchScoreUpgradeClick({ page, action: "buy_scan" });
};

export const trackUpgradeClick = (planTier, page) => {
  trackEvent("upgrade_click", "monetization", { plan_tier: planTier, page });
  trackMatchScoreUpgradeClick({ plan_tier: planTier, page });
};

export const trackFundWalletClick = (source) => {
  trackEvent("fund_wallet_click", "monetization", { source });
  trackEvent("wallet_funding_clicks", "monetization", { source });
};

export const trackPaymentInitiated = (planId, planTier, extraProps = {}) => {
  trackEvent("payment_initiated", "monetization", {
    plan_id: planId,
    plan_tier: planTier,
    ...extraProps,
  });
  trackEvent("wallet_funding_clicks", "monetization", {
    plan_id: planId,
    plan_tier: planTier,
    action: "checkout_started",
    ...extraProps,
  });
};

export const trackJaraPayPaymentClick = (planId, amount, extraProps = {}) => {
  trackEvent("jarapay_payment_clicks", "monetization", {
    plan_id: planId || null,
    amount: amount || null,
    payment_method: "jarapay",
    ...extraProps,
  });
};

export const trackPaymentCompleted = (props = {}) => {
  trackEvent("payment_completed", "monetization", props);
  // Conforms with PRD nomenclature for Pillar 4 Step 4
  trackEvent("jarapay_payment_clicks", "monetization", {
    ...props,
    payment_status: "completed",
  });
};

export const trackSubscriptionActivated = (props = {}) => {
  trackEvent("subscription_activated", "monetization", props);
  trackPaymentCompleted(props);
};

export const trackTrialEvent = (eventName, extra = {}) => {
  trackEvent(eventName, "monetization", extra);
};

/* =========================================================================
   PILLAR 5: VIRAL NETWORK INVITATIONS (PRD §9)
   ========================================================================= */
export const trackInviteNavClick = (extraProps = {}) => {
  trackEvent("invite_nav_clicks", "grow_network", extraProps);
};

export const trackInviteCopyLinkClick = (extraProps = {}) => {
  trackEvent("invite_copy_link_clicks", "grow_network", extraProps);
};

export const trackImportContactsClick = (extraProps = {}) => {
  trackEvent("import_contacts_clicks", "grow_network", extraProps);
};

export const trackImportContactsConsentClick = (extraProps = {}) => {
  trackEvent("import_contacts_consent_clicks", "grow_network", extraProps);
};

export const trackGrowNetworkCardView = () => {
  trackEvent("grow_network_card_view", "grow_network", {});
};

export const trackGrowNetworkCardClick = (action = "import") => {
  trackEvent("grow_network_card_click", "grow_network", { action });
};

export const trackContactsImportStarted = (source) => {
  trackEvent("contacts_import_started", "grow_network", { source });
  trackImportContactsClick({ source });
};

export const trackContactsImportCompleted = (source, count) => {
  trackEvent("contacts_import_completed", "grow_network", { source, count });
  trackImportContactsConsentClick({ source, count });
};

export const trackInvitesSent = (count) => {
  trackEvent("invites_sent", "grow_network", { count });
};

export const trackInviteConverted = (props = {}) => {
  trackEvent("invite_converted", "grow_network", props);
};

export const trackMilestoneReached = (milestoneNumber) => {
  trackEvent("milestone_reached", "grow_network", {
    milestone_number: milestoneNumber,
  });
};

export const trackGrowNetworkNudgeShown = () => {
  trackEvent("grow_network_nudge_shown", "grow_network", {});
};

export const trackGrowNetworkNudgeClicked = () => {
  trackEvent("grow_network_nudge_clicked", "grow_network", {});
};

/* =========================================================================
   PILLAR 6: APPLICATION DISCOVERY & PIPELINE
   ========================================================================= */
export const trackJobCardClick = (jobId, jobTitle, extraProps = {}) => {
  trackEvent("jobcard_click", "applications", {
    job_id: jobId,
    job_title: jobTitle,
    ...extraProps,
  });
};

export const trackJobView = (jobId, jobTitle, companyName) => {
  trackEvent("job_view", "applications", {
    job_id: jobId,
    job_title: jobTitle,
    company_name: companyName,
  });
};

export const trackJobSave = (jobId, jobTitle) => {
  trackEvent("job_save", "applications", { job_id: jobId, job_title: jobTitle });
};

export const trackJobLike = (jobId, jobTitle) => {
  trackEvent("job_like", "applications", { job_id: jobId, job_title: jobTitle });
};

export const trackJobCardApplyClick = (jobId, jobTitle, extraProps = {}) => {
  trackEvent("jobcard_apply_click", "applications", {
    job_id: jobId,
    job_title: jobTitle,
    ...extraProps,
  });
};

export const trackJobCardMatchScoreClick = (jobId, matchScoreValue, extraProps = {}) => {
  trackEvent("jobcard_matchscore_click", "applications", {
    job_id: jobId,
    match_score_value: matchScoreValue !== undefined ? matchScoreValue : null,
    ...extraProps,
  });
};

export const trackJobCardUpgradeClick = (jobId, extraProps = {}) => {
  trackEvent("jobcard_upgrade_click", "applications", {
    job_id: jobId,
    ...extraProps,
  });
  // Also track under monetization
  trackMatchScoreUpgradeClick({
    job_id: jobId,
    source: "jobcard_upgrade_click",
    ...extraProps,
  });
};

export const trackJobApplicationSubmitted = (jobId, extraProps = {}) => {
  trackEvent("job_application_submitted", "applications", {
    job_id: jobId,
    ...extraProps,
  });
  trackEvent("job_apply", "applications", {
    job_id: jobId,
    ...extraProps,
  });
};

export const trackJobApply = (jobId, jobTitle) => {
  trackJobApplicationSubmitted(jobId, { job_title: jobTitle });
};

export const trackResumeUpload = () => {
  trackEvent("resume_upload", "profile", {});
};

export const trackProfileUpdate = (fields = []) => {
  trackEvent("profile_update", "profile", { updated_fields: fields });
};

const flushEvents = () => {
  if (eventQueue.length === 0 || !API_URL) return;

  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);
  const payload = JSON.stringify({ events: batch });
  const headers = { "Content-Type": "application/json" };
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    fetch(`${API_URL}/events/track`, {
      method: "POST",
      headers,
      body: payload,
      keepalive: true,
    }).catch(() => {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            `${API_URL}/events/track`,
            new Blob([payload], { type: "text/plain;charset=UTF-8" })
          );
        }
      } catch (e) {
        // analytics should never break the app
      }
    });
  } catch (e) {
    // Silently fail -- analytics should never break the app
  }
};

export const initAnalytics = () => {
  if (isInitialized) return;
  isInitialized = true;

  initGtmDataLayer();

  flushTimer = setInterval(flushEvents, FLUSH_INTERVAL_MS);

  window.addEventListener("beforeunload", flushEvents);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEvents();
    }
  });
};

export const shutdownAnalytics = () => {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flushEvents();
  isInitialized = false;
};

export default {
  init: initAnalytics,
  shutdown: shutdownAnalytics,
  pushGtmPageView,
  track: trackEvent,
  pageView: trackPageView,
  // Pillar 1
  signupPageView: trackSignupPageView,
  signupAttributed: trackSignupAttributed,
  signupCompleted: trackSignupCompleted,
  accountActivated: trackAccountActivated,
  signupDropoff: trackSignupDropoff,
  // Pillar 2
  postJobClick: trackPostJobClick,
  jobUploadExcelClick: trackJobUploadExcelClick,
  jobPostNewClick: trackJobPostNewClick,
  jobPostPublishClick: trackJobPostPublishClick,
  firstJobPosted: trackFirstJobPosted,
  jobPostVerificationClick: trackJobPostVerificationClick,
  // Pillar 3
  referButtonClick: trackReferButtonClick,
  referralLinkCopied: trackReferralLinkCopied,
  firstReferralCompleted: trackFirstReferralCompleted,
  userReferralSuccess: trackUserReferralSuccess,
  // Pillar 4
  jobMatchScoreClick: trackJobMatchScoreClick,
  jobMatchScoreCompleted: trackJobMatchScoreCompleted,
  matchScore: trackMatchScore,
  matchScoreUpgradeClick: trackMatchScoreUpgradeClick,
  buyScanClick: trackBuyScanClick,
  upgradeClick: trackUpgradeClick,
  fundWalletClick: trackFundWalletClick,
  paymentInitiated: trackPaymentInitiated,
  jaraPayPaymentClick: trackJaraPayPaymentClick,
  paymentCompleted: trackPaymentCompleted,
  subscriptionActivated: trackSubscriptionActivated,
  // Pillar 5
  inviteNavClick: trackInviteNavClick,
  inviteCopyLinkClick: trackInviteCopyLinkClick,
  importContactsClick: trackImportContactsClick,
  importContactsConsentClick: trackImportContactsConsentClick,
  growNetworkCardView: trackGrowNetworkCardView,
  growNetworkCardClick: trackGrowNetworkCardClick,
  contactsImportStarted: trackContactsImportStarted,
  contactsImportCompleted: trackContactsImportCompleted,
  invitesSent: trackInvitesSent,
  inviteConverted: trackInviteConverted,
  milestoneReached: trackMilestoneReached,
  growNetworkNudgeShown: trackGrowNetworkNudgeShown,
  growNetworkNudgeClicked: trackGrowNetworkNudgeClicked,
  // Pillar 6
  jobCardClick: trackJobCardClick,
  jobView: trackJobView,
  jobSave: trackJobSave,
  jobLike: trackJobLike,
  jobCardApplyClick: trackJobCardApplyClick,
  jobCardMatchScoreClick: trackJobCardMatchScoreClick,
  jobCardUpgradeClick: trackJobCardUpgradeClick,
  jobApplicationSubmitted: trackJobApplicationSubmitted,
  jobApply: trackJobApply,
  // Profile
  resumeUpload: trackResumeUpload,
  profileUpdate: trackProfileUpdate,
};
